"use client";
import { T, useTextLocale, tl } from "@/components/t";
import Link from "next/link";

import { useState, useRef } from "react";
import {
  Upload, FileText, Loader2, AlertTriangle, CheckCircle, Info, ArrowRight,
  ChevronLeft, Sparkles, TrendingUp, Receipt, HeartPulse, Shield, Euro, Eye, Image as ImageIcon,
} from "lucide-react";

const API = process.env.NEXT_PUBLIC_API_URL || "";

interface Deduction {
  label: string; amount: number; category: string;
  explanation: string; is_optimizable: boolean;
  optimization_tip?: string;
}
interface Optimization {
  title: string; potential_savings_yearly: number;
  effort: string; description: string; next_step: string;
}
interface PayslipResult {
  summary: { brutto: number; netto: number; deduction_pct: number; month?: string; employer?: string; tax_class?: string; kv_zusatz?: number; bundesland?: string; headline: string; };
  deductions: Deduction[];
  totals: { total_taxes: number; total_social: number; total_deductions: number; tax_pct: number; social_pct: number };
  what_you_get: { label: string; value: string }[];
  optimization_opportunities: Optimization[];
  red_flags?: { title: string; description: string }[];
  vergleich_zu_gleichaltrigen?: { income_percentile: number; deduction_percentile: number; context: string };
  is_example?: boolean;
}

export default function PayslipExplainerPage() {
  const loc = useTextLocale();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<PayslipResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showExample, setShowExample] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const onPick = (f: File) => {
    setFile(f);
    setError(null);
    setResult(null);
    if (f.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = e => setPreview(e.target?.result as string);
      reader.readAsDataURL(f);
    } else {
      setPreview(null);
    }
  };

  const upload = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await fetch(`${API}/api/finance/payslip/explain`, { method: "POST", body: fd });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || tl(loc, "Analyse fehlgeschlagen", "Analysis failed"));
      }
      setResult(await r.json());
    } catch (e) {
      setError((e as Error).message);
    }
    setLoading(false);
  };

  const showExampleData = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/finance/payslip/example`);
      if (r.ok) setResult(await r.json());
    } catch {}
    setLoading(false);
    setShowExample(true);
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
    setShowExample(false);
  };

  /* ── Result view ── */
  if (result) {
    return (
      <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6">
        <button onClick={reset} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary">
          <ChevronLeft className="h-4 w-4" /> <T de="Andere Lohnabrechnung" en="Different payslip" />
        </button>

        {result.is_example && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 flex items-center gap-3">
            <Info className="h-5 w-5 text-amber-700 flex-shrink-0" />
            <p className="text-sm text-amber-900"><T de="Das ist ein Beispiel. Lade deine eigene Lohnabrechnung hoch, um deine Zahlen zu sehen." en="This is an example. Upload your own payslip to see your numbers." /></p>
          </div>
        )}

        {/* Hero */}
        <div className="rounded-2xl border bg-card overflow-hidden">
          <div className="bg-primary/10 p-6 md:p-8">
            <p className="text-xs font-bold text-primary tracking-widest uppercase mb-2"><T de="Deine Lohnabrechnung" en="Your payslip" /></p>
            <h1 className="text-2xl md:text-3xl font-extrabold text-foreground leading-tight">{result.summary.headline}</h1>
            {result.summary.month && (
              <p className="text-sm text-muted-foreground mt-2">{result.summary.month}{result.summary.employer ? ` · ${result.summary.employer}` : ""}</p>
            )}
          </div>
          <div className="p-6 grid grid-cols-3 gap-3">
            <div className="text-center">
              <p className="text-xs text-muted-foreground"><T de="Brutto" en="Gross" /></p>
              <p className="text-2xl md:text-3xl font-black text-foreground">{result.summary.brutto.toLocaleString("de-DE")}€</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground"><T de="Abzüge" en="Deductions" /></p>
              <p className="text-2xl md:text-3xl font-black text-destructive">−{(result.summary.brutto - result.summary.netto).toLocaleString("de-DE")}€</p>
              <p className="text-[10px] text-destructive">({result.summary.deduction_pct}%)</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground"><T de="Netto" en="Net" /></p>
              <p className="text-2xl md:text-3xl font-black text-emerald-700">{result.summary.netto.toLocaleString("de-DE")}€</p>
            </div>
          </div>
        </div>

        {/* Red flags first if any */}
        {result.red_flags && result.red_flags.length > 0 && (
          <div className="rounded-2xl border-2 border-destructive/30 bg-destructive/5 p-5">
            <p className="text-sm font-bold text-destructive uppercase tracking-widest mb-3">⚠️ <T de="Auffälligkeiten" en="Irregularities" /></p>
            <div className="space-y-2">
              {result.red_flags.map((f, i) => (
                <div key={i}><p className="text-sm font-bold text-foreground">{f.title}</p><p className="text-sm text-muted-foreground">{f.description}</p></div>
              ))}
            </div>
          </div>
        )}

        {/* Each deduction explained */}
        <div>
          <p className="text-xs font-bold text-muted-foreground tracking-widest uppercase mb-3"><T de="Jede Abzug erklärt" en="Every deduction explained" /></p>
          <div className="space-y-2">
            {result.deductions.map((d, i) => (
              <details key={i} className="group rounded-xl border bg-card overflow-hidden">
                <summary className="cursor-pointer flex items-center gap-3 p-4 hover:bg-secondary/30 transition-colors list-none">
                  <span className={`flex h-9 w-9 items-center justify-center rounded-lg flex-shrink-0 ${
                    d.category === "steuer" ? "bg-destructive/10 text-destructive" :
                    d.category === "sozialversicherung" ? "bg-primary/10 text-primary" :
                    "bg-secondary text-muted-foreground"
                  }`}>
                    {d.category === "steuer" ? <Receipt className="h-4 w-4" /> : <HeartPulse className="h-4 w-4" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground">{d.label}</p>
                  </div>
                  <span className="text-base font-black text-foreground">−{d.amount.toLocaleString("de-DE", { minimumFractionDigits: 2 })}€</span>
                  {d.is_optimizable && <span className="text-[10px] font-bold bg-emerald-500 text-white px-1.5 py-0.5 rounded"><T de="SPARBAR" en="SAVEABLE" /></span>}
                </summary>
                <div className="px-4 pb-4 pl-16">
                  <p className="text-sm text-muted-foreground leading-relaxed">{d.explanation}</p>
                  {d.optimization_tip && (
                    <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                      <p className="text-xs font-bold text-emerald-700 uppercase mb-1">💡 <T de="Spar-Tipp" en="Saving tip" /></p>
                      <p className="text-sm text-emerald-900">{d.optimization_tip}</p>
                    </div>
                  )}
                </div>
              </details>
            ))}
          </div>
        </div>

        {/* Was du dafür bekommst */}
        {result.what_you_get?.length > 0 && (
          <div className="rounded-2xl border bg-card p-5">
            <p className="text-base font-bold text-foreground mb-3"><T de="Was du für deine Sozialabgaben bekommst" en="What you get for your social contributions" /></p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {result.what_you_get.map((w, i) => (
                <div key={i} className="flex items-start gap-3 rounded-lg bg-secondary/40 p-3">
                  <CheckCircle className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div><p className="text-sm font-semibold text-foreground">{w.label}</p><p className="text-xs text-muted-foreground">{w.value}</p></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Optimization opportunities */}
        {result.optimization_opportunities?.length > 0 && (
          <div>
            <p className="text-xs font-bold text-muted-foreground tracking-widest uppercase mb-3">💰 <T de="Hier kannst du sparen" en="Here you can save" /></p>
            <div className="space-y-3">
              {result.optimization_opportunities.map((o, i) => (
                <div key={i} className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-5">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-base font-bold text-foreground">{o.title}</p>
                      <p className="text-xs text-muted-foreground"><T de="Aufwand:" en="Effort:" /> {o.effort}</p>
                    </div>
                    <p className="text-xl font-black text-emerald-700">+{o.potential_savings_yearly.toLocaleString("de-DE")}€<span className="text-xs font-normal text-muted-foreground ml-1">/<T de="Jahr" en="year" /></span></p>
                  </div>
                  <p className="text-sm text-foreground mt-2 mb-3">{o.description}</p>
                  <p className="text-sm font-semibold text-emerald-900">→ {o.next_step}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Peer comparison */}
        {result.vergleich_zu_gleichaltrigen && (
          <div className="rounded-2xl border bg-card p-5">
            <p className="text-base font-bold text-foreground mb-2"><T de="Du vs. andere unter 30" en="You vs. others under 30" /></p>
            <p className="text-sm text-muted-foreground mb-4">{result.vergleich_zu_gleichaltrigen.context}</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground"><T de="Einkommen" en="Income" /></p>
                <p className="text-2xl font-black text-primary">Top {100 - result.vergleich_zu_gleichaltrigen.income_percentile}%</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground"><T de="Abzugsquote" en="Deduction rate" /></p>
                <p className="text-2xl font-black text-foreground">{result.vergleich_zu_gleichaltrigen.deduction_percentile}. <T de="Perzentil" en="Percentile" /></p>
              </div>
            </div>
          </div>
        )}

        {/* CTA back to dashboard */}
        <Link href="/finanzen" className="block rounded-2xl bg-primary text-white p-6 hover:opacity-95 transition-opacity">
          <p className="text-sm font-bold opacity-80 uppercase tracking-widest mb-1"><T de="Was als Nächstes?" en="What's next?" /></p>
          <p className="text-xl font-bold"><T de="Sieh deinen Finanz-Score → personalisierte Tipps für dich" en="See your financial score → personalised tips for you" /></p>
          <p className="text-sm opacity-90 mt-2"><T de="Zurück zum Finanzwissen-Dashboard" en="Back to the finance dashboard" /> <ArrowRight className="inline h-4 w-4 ml-1" /></p>
        </Link>
      </div>
    );
  }

  /* ── Upload view ── */
  return (
    <div className="max-w-3xl mx-auto p-4 md:p-6 space-y-6">
      <Link href="/finanzen" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary">
        <ChevronLeft className="h-4 w-4" /> <T de="Zurück zu Finanzen" en="Back to finances" />
      </Link>

      <div>
        <p className="text-sm font-medium text-primary tracking-wide uppercase mb-1"><T de="Lohnabrechnung verstehen" en="Understand your payslip" /></p>
        <h1 className="text-3xl md:text-4xl font-extrabold text-foreground tracking-tight"><T de="Was bedeutet jede Zeile auf deiner Gehaltsabrechnung?" en="What does every line on your payslip mean?" /></h1>
        <p className="text-base text-muted-foreground mt-2"><T de="Lade deine Lohnabrechnung hoch, wir erklären dir jede Position und zeigen, wo du sparen kannst." en="Upload your payslip, we explain every item and show where you can save." /></p>
      </div>

      {/* Upload zone */}
      <div className="rounded-2xl border-2 border-dashed border-primary/30 bg-primary/[0.02] p-8 text-center">
        {file ? (
          <div className="space-y-4">
            {preview ? (
              <img src={preview} alt={tl(loc, "Vorschau", "Preview")} className="mx-auto max-h-64 rounded-lg shadow-lg" />
            ) : (
              <div className="flex items-center justify-center gap-3 text-primary">
                <FileText className="h-8 w-8" />
                <span className="text-base font-semibold">{file.name}</span>
              </div>
            )}
            <div className="flex gap-3 justify-center">
              <button onClick={() => { setFile(null); setPreview(null); }}
                className="rounded-xl border border-border px-5 py-2.5 text-sm font-medium text-muted-foreground hover:border-muted-foreground">
                <T de="Andere Datei" en="Different file" />
              </button>
              <button onClick={upload} disabled={loading}
                className="rounded-xl bg-primary text-white px-6 py-2.5 text-sm font-bold hover:bg-[#0F00D6] disabled:opacity-50 flex items-center gap-2">
                {loading
                  ? <><Loader2 className="h-4 w-4 animate-spin" /> <T de="Wird analysiert…" en="Analysing…" /></>
                  : <><Sparkles className="h-4 w-4" /> <T de="Jetzt erklären" en="Explain now" /></>}
              </button>
            </div>
          </div>
        ) : (
          <div onClick={() => inputRef.current?.click()} className="cursor-pointer space-y-3">
            <Upload className="h-12 w-12 text-primary mx-auto" />
            <div>
              <p className="text-base font-bold text-foreground"><T de="Klick hier oder zieh deine Lohnabrechnung rein" en="Click here or drag your payslip in" /></p>
              <p className="text-sm text-muted-foreground mt-1"><T de="PDF, PNG, JPG · max 10 MB · 100% privat (keine Speicherung)" en="PDF, PNG, JPG · max 10 MB · 100% private (no storage)" /></p>
            </div>
          </div>
        )}
        <input ref={inputRef} type="file" accept="image/*,application/pdf" hidden
          onChange={e => e.target.files?.[0] && onPick(e.target.files[0])} />
      </div>

      {error && (
        <div className="rounded-xl bg-destructive/10 border border-destructive/30 p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
          <p className="text-sm text-foreground">{error}</p>
        </div>
      )}

      {/* Demo CTA */}
      {!file && (
        <div className="rounded-2xl bg-secondary p-5 text-center">
          <p className="text-sm text-muted-foreground mb-3"><T de="Erst mal anschauen, was du bekommst?" en="Want to see what you get first?" /></p>
          <button onClick={showExampleData} disabled={loading}
            className="rounded-xl bg-card border border-primary/20 px-5 py-2.5 text-sm font-bold text-primary hover:bg-primary/5 transition-colors flex items-center gap-2 mx-auto">
            <Eye className="h-4 w-4" /> <T de="Beispiel-Analyse anzeigen" en="Show example analysis" />
          </button>
        </div>
      )}

      {/* What you get */}
      <div className="rounded-2xl border bg-card p-6">
        <p className="text-base font-bold text-foreground mb-4"><T de="Was du bekommst" en="What you get" /></p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { i: Receipt, tDe: "Jede Abzug-Position erklärt", tEn: "Every deduction explained", sDe: "Lohnsteuer, Soli, KV, RV, AV, alles auf einen Blick", sEn: "Income tax, solidarity surcharge, health/pension/unemployment insurance, all at a glance" },
            { i: TrendingUp, tDe: "Konkrete Spar-Tipps", tEn: "Concrete saving tips", sDe: "Wo du wirklich Geld zurückholen kannst", sEn: "Where you can actually get money back" },
            { i: Shield, tDe: "Auffälligkeiten gemeldet", tEn: "Irregularities flagged", sDe: "Falsche Steuerklasse, fehlende Beiträge etc.", sEn: "Wrong tax class, missing contributions etc." },
            { i: Sparkles, tDe: "Was du dafür bekommst", tEn: "What you get for it", sDe: "Welche Leistungen deine Sozialabgaben sichern", sEn: "What benefits your social contributions secure" },
          ].map((b, i) => {
            const Ic = b.i;
            return (
              <div key={i} className="flex items-start gap-3">
                <Ic className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-foreground">{tl(loc, b.tDe, b.tEn)}</p>
                  <p className="text-xs text-muted-foreground">{tl(loc, b.sDe, b.sEn)}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
